/**
 * Діалог у трьох режимах: читання, аудіювання (переклад прихований)
 * і практика — репліки учня закриті, треба згадати їх самому.
 */
import { useState } from 'react'
import { useStore, showTranscription } from '../lib/store.ts'
import { Speak, TopBar } from '../components/ui.tsx'
import { play, unlockAudio } from '../lib/audio.ts'

type Mode = 'read' | 'listen' | 'practice'

export function DialogueView({ moduleId, id }: { moduleId: string; id: string }) {
  const { idx, settings, now } = useStore()
  const m = idx.moduleById.get(moduleId)
  const d = m?.dialogues.find((x) => x.id === id)
  const [mode, setMode] = useState<Mode>('read')
  const [revealed, setRevealed] = useState<Set<number>>(new Set())
  const [playingAll, setPlayingAll] = useState(false)

  if (!m || !d) return <div className="screen">Діалог не знайдено</div>
  const showTr = showTranscription(settings, now)

  const playAll = async () => {
    unlockAudio()
    setPlayingAll(true)
    for (const line of d.lines) {
      await play(line.audio, { text: line.pl })
      await new Promise((r) => setTimeout(r, 260))
    }
    setPlayingAll(false)
  }

  const reveal = (i: number) => setRevealed((s) => new Set(s).add(i))

  return (
    <>
      <TopBar title={d.title} />
      <div className="screen stack">
        <p className="muted small">{d.setting}</p>

        <div className="row" style={{ gap: 6 }}>
          {([['read', 'Читати'], ['listen', 'На слух'], ['practice', 'Практика']] as const).map(([k, label]) => (
            <button
              key={k}
              className={`chip${mode === k ? ' chip--good' : ''}`}
              style={{ padding: '7px 13px', fontSize: 13 }}
              onClick={() => { setMode(k); setRevealed(new Set()) }}
            >
              {label}
            </button>
          ))}
          <div className="spacer" />
          <button className="btn btn--ghost" style={{ minHeight: 36, padding: '0 12px' }} onClick={playAll} disabled={playingAll}>
            {playingAll ? '▶︎ грає…' : '▶︎ увесь діалог'}
          </button>
        </div>

        {mode === 'practice' && (
          <div className="feedback feedback--hint">
            Твої репліки приховані. Спробуй сказати вголос, потім відкрий і порівняй.
          </div>
        )}

        <div className="stack">
          {d.lines.map((line, i) => {
            const isYou = line.speaker === 'you'
            const hidden = mode === 'practice' && isYou && !revealed.has(i)
            return (
              <div className={`line${isYou ? ' line--you' : ''}`} key={i}>
                <Speak audio={line.audio} text={line.pl} small />
                <div className="bubble" onClick={() => hidden && reveal(i)}>
                  {hidden ? (
                    <>
                      <div className="bubble__uk" style={{ marginTop: 0 }}>{line.uk}</div>
                      <div className="tiny faint" style={{ marginTop: 6 }}>Натисни, щоб побачити польською</div>
                    </>
                  ) : (
                    <>
                      <div className="bubble__pl">{line.pl}</div>
                      {mode !== 'listen' && <div className="bubble__uk">{line.uk}</div>}
                      {showTr && isYou && line.tr && mode !== 'listen' && <div className="bubble__tr">{line.tr}</div>}
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {mode === 'listen' && (
          <p className="tiny faint center">
            Переклад прихований навмисно: слухай і намагайся зрозуміти зі звучання.
          </p>
        )}

        <a className="btn btn--wide" href={`#/module/${m.id}`}>Назад до модуля</a>
      </div>
    </>
  )
}
